import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import Title from '@/components/ui/Title';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import type { SurveyQuestion, SurveyTemplate } from '@/features/webinar/types';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { webinarService } from '@/services/webinar.service';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/app/hooks';
import Loader from '@/components/ui/Loader';
import { toast } from 'react-toastify';
import type { AppError } from '@/features/auth/types';
import { normalizeAxiosError } from '@/http/errors';
import { mapHttpError } from '@/lib/mapApiError';
import PreviewActivitySurvey from './PreviewActivitySurvey';
import { Skeleton } from '@/components/ui/skeleton';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

type SurveyFormValues = {
  title: string;
  subtitle: string;
  questions: {
    id: number;
    question: string;
    answerType: '1' | '2';
    answers: string[];
  }[];
};

interface SurveyFormProps {
  webinarId: number;
  workspaceId: number;
  visualIdentity: WorkspaceVisualIdentity;
  showSurvey: boolean;
  onFormReady?: (form: UseFormReturn<SurveyFormValues>) => void;
  isEdit?: boolean;
  isEvergreen?: boolean;
}

export default function SurveyFormWaitingRoom({
  webinarId,
  workspaceId,
  visualIdentity,
  showSurvey,
  onFormReady,
  isEvergreen,
}: SurveyFormProps) {
  const { t } = useTranslation('webinars');
  const { t: tCW } = useTranslation('createWebinar');
  const currentWaitingRoomPage = useAppSelector((state) => state.webinar.currentWaitingRoomPage);
  const [isLoading, setIsLoading] = useState(true);
  const [surveyStep, setSurveyStep] = useState(1);
  const [selectedAnswers, setSelectedAnswers] = useState<{
    [questionId: number]: string;
  }>({});
  const [accordionValue, setAccordionValue] = useState<string>(isEvergreen ? '' : 'item-1');
  const [existingSurveyTemplate, setExistingSurveyTemplate] = useState<SurveyTemplate | null>(null);
  console.log('showSurvey', showSurvey);

  const [, setQuestions] = useState<
    {
      id: number;
      question: string;
      answerType: '1' | '2';
      answers: string[];
    }[]
  >([
    {
      id: -1,
      question: '',
      answerType: '1',
      answers: ['', ''],
    },
  ]);

  const surveyFormSchema = useMemo(
    () =>
      z.object({
        title: z.string().min(1, {
          message: t('waitingRoom.survey.validation.titleRequired'),
        }),
        subtitle: z.string().min(1, {
          message: t('waitingRoom.survey.validation.subtitleRequired'),
        }),
        questions: z
          .array(
            z.object({
              id: z.number(),
              question: z.string().min(1, {
                message: t('waitingRoom.survey.validation.questionRequired'),
              }),
              answerType: z.enum(['1', '2'], {
                message: t('waitingRoom.survey.validation.answerTypeRequired'),
              } as any),
              answers: z
                .array(
                  z.string().min(1, {
                    message: t('waitingRoom.survey.validation.answerNotEmpty'),
                  }),
                )
                .min(2, {
                  message: t('waitingRoom.survey.validation.minAnswersRequired'),
                })
                .max(6, {
                  message: t('waitingRoom.survey.validation.maxAnswersExceeded'),
                }),
            }),
          )
          .min(1, {
            message: t('waitingRoom.survey.validation.minQuestionsRequired'),
          }),
      }),
    [t],
  );

  const form = useForm<SurveyFormValues>({
    resolver: zodResolver(surveyFormSchema),
    defaultValues: {
      title: '',
      subtitle: '',
      questions: [
        {
          id: -1,
          question: '',
          answerType: '1',
          answers: ['', ''],
        },
      ],
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (onFormReady) {
      onFormReady(form);
    }
  }, [form, onFormReady]);

  useEffect(() => {
    if (isEvergreen) {
      setAccordionValue('');
    } else if (showSurvey) {
      setAccordionValue('item-1');
    } else {
      setAccordionValue('');
    }
  }, [showSurvey, isEvergreen]);

  useEffect(() => {
    const fetchAndPreFillSurvey = async () => {
      if (!webinarId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const templatesPage = await webinarService.listSurveyTemplates(
          workspaceId,
          webinarId,
          0,
          50,
        );

        const waitingRoomTemplate = templatesPage.content.find(
          (template) => template.waitingRoomSurvey === true,
        );

        if (waitingRoomTemplate) {
          console.log(
            "Survey de salle d'attente trouvé, préremplissage du formulaire:",
            waitingRoomTemplate,
          );
          setExistingSurveyTemplate(waitingRoomTemplate);

          const sortedQuestions = [...waitingRoomTemplate.questions].sort(
            (a, b) => a.position - b.position,
          );

          const formQuestions = sortedQuestions.map((question) => {
            const answerType: '1' | '2' = question.type === 'MULTIPLE_CHOICE' ? '2' : '1';

            const answers = question.options
              .sort((a, b) => a.position - b.position)
              .map((option) => option.textContent);

            return {
              id: question.id,
              question: question.textContent || '',
              answerType,
              answers: answers.length >= 2 ? answers : ['', ''],
            };
          });

          const finalQuestions =
            formQuestions.length > 0
              ? formQuestions
              : [
                  {
                    id: -1,
                    question: '',
                    answerType: '1' as const,
                    answers: ['', ''],
                  },
                ];

          const formData = {
            title: waitingRoomTemplate.title || '',
            subtitle: waitingRoomTemplate.subTitle || '',
            questions: finalQuestions,
          };

          console.log('Données formatées pour le formulaire:', formData);
          form.reset(formData);
          setQuestions(finalQuestions);
        } else {
          console.log("Aucun survey de salle d'attente trouvé, formulaire vide");
          setExistingSurveyTemplate(null);
        }
      } catch (error) {
        console.error("Erreur lors du fetch du survey de salle d'attente:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndPreFillSurvey();
  }, [webinarId, workspaceId, form]);

  const handleAddAnswer = (questionId: number) => {
    const currentQuestions = form.getValues('questions') as {
      id: number;
      question: string;
      answerType: '1' | '2';
      answers: string[];
    }[];
    const updatedQuestions = currentQuestions.map((q) =>
      q.id === questionId && q.answers.length < 6 ? { ...q, answers: [...q.answers, ''] } : q,
    );
    form.setValue('questions', updatedQuestions as any);
    setQuestions(updatedQuestions as any);
  };

  const handleRemoveAnswer = (questionId: number, answerIndex: number) => {
    const currentQuestions = form.getValues('questions') as {
      id: number;
      question: string;
      answerType: '1' | '2';
      answers: string[];
    }[];
    const updatedQuestions = currentQuestions.map((q) =>
      q.id === questionId && q.answers.length > 2
        ? { ...q, answers: q.answers.filter((_, i) => i !== answerIndex) }
        : q,
    );
    form.setValue('questions', updatedQuestions as any);
    setQuestions(updatedQuestions as any);
  };

  const handleAddQuestion = () => {
    const currentQuestions = form.getValues('questions') as {
      id: number;
      question: string;
      answerType: '1' | '2';
      answers: string[];
    }[];
    const minId = currentQuestions.length > 0 ? Math.min(...currentQuestions.map((q) => q.id)) : 0;
    const newQuestionId = minId <= 0 ? minId - 1 : -1;
    const updatedQuestions = [
      ...currentQuestions,
      {
        id: newQuestionId,
        question: '',
        answerType: '1',
        answers: ['', ''],
      },
    ];
    form.setValue('questions', updatedQuestions as any);
    setQuestions(updatedQuestions as any);
  };

  const handleRemoveQuestion = (questionId: number) => {
    const currentQuestions = form.getValues('questions') as {
      id: number;
      question: string;
      answerType: '1' | '2';
      answers: string[];
    }[];
    if (currentQuestions.length > 1) {
      const updatedQuestions = currentQuestions.filter((q) => q.id !== questionId);
      form.setValue('questions', updatedQuestions as any);
      setQuestions(updatedQuestions as any);
    }
  };

  const onSubmit = async (data: SurveyFormValues) => {
    if (!webinarId || !workspaceId) {
      console.error("Impossible de sauvegarder le sondage de salle d'attente : webinar manquant");
      return;
    }

    try {
      if (!data.questions || data.questions.length === 0) {
        console.error("Aucune question à sauvegarder pour le sondage de salle d'attente");
        return;
      }

      const templatesPage = await webinarService.listSurveyTemplates(workspaceId, webinarId, 0, 50);
      const existingWaitingRoomTemplate: SurveyTemplate | undefined = templatesPage.content.find(
        (t) => t.waitingRoomSurvey === true,
      );

      const templateInput = {
        title: data.title.trim(),
        subTitle: data.subtitle.trim(),
        waitingRoomSurvey: true,
        displayWaitingRoomSurvey: showSurvey,
      };

      let templateId: number;

      console.log('templateInput', templateInput);

      if (existingWaitingRoomTemplate) {
        // Le sondage existe, on le met à jour
        templateId = existingWaitingRoomTemplate.id;
        await webinarService.updateSurveyTemplate(
          workspaceId,
          webinarId,
          templateId,
          templateInput,
        );

        const currentTemplate = await webinarService.getSurveyTemplate(
          workspaceId,
          webinarId,
          templateId,
        );

        const existingQuestionsMap = new Map<number, SurveyQuestion>();
        currentTemplate.questions.forEach((q) => {
          const formQuestion = data.questions.find((fq) => fq.id === q.id && fq.id > 0);
          if (formQuestion) {
            existingQuestionsMap.set(q.id, q);
          }
        });

        const processedQuestionIds: number[] = [];

        for (const [questionIndex, formQuestion] of data.questions.entries()) {
          const questionType =
            formQuestion.answerType === '2' ? 'MULTIPLE_CHOICE' : 'SINGLE_CHOICE';
          let questionId: number;

          if (formQuestion.id > 0 && existingQuestionsMap.has(formQuestion.id)) {
            const existingQuestion = existingQuestionsMap.get(formQuestion.id)!;
            questionId = existingQuestion.id;

            await webinarService.updateSurveyQuestion(
              workspaceId,
              webinarId,
              templateId,
              questionId,
              {
                textContent: formQuestion.question.trim(),
                type: questionType,
                position: questionIndex,
                required: false,
                maxSelections: null,
                randomizeOptions: false,
              },
            );

            // Supprimer toutes les options existantes
            for (const option of existingQuestion.options) {
              await webinarService.deleteSurveyAnswerOption(
                workspaceId,
                webinarId,
                templateId,
                questionId,
                option.id,
              );
            }
          } else {
            // La question est nouvelle, on la crée
            questionId = (await webinarService.createSurveyQuestion(
              workspaceId,
              webinarId,
              templateId,
              {
                textContent: formQuestion.question.trim(),
                type: questionType,
                position: questionIndex,
                required: false,
                maxSelections: null,
                randomizeOptions: false,
              },
            )) as number;
          }

          processedQuestionIds.push(questionId);

          // Créer les nouvelles options
          for (const [answerIndex, answer] of formQuestion.answers.entries()) {
            if (answer.trim()) {
              await webinarService.createSurveyAnswerOption(
                workspaceId,
                webinarId,
                templateId,
                questionId,
                {
                  textContent: answer.trim(),
                  position: answerIndex,
                },
              );
            }
          }
        }

        // Supprimer les questions qui ne sont plus dans le formulaire
        const questionsToDelete = currentTemplate.questions.filter(
          (q) => !processedQuestionIds.includes(q.id),
        );

        for (const questionToDelete of questionsToDelete) {
          // Supprimer toutes les options de la question
          for (const option of questionToDelete.options) {
            await webinarService.deleteSurveyAnswerOption(
              workspaceId,
              webinarId,
              templateId,
              questionToDelete.id,
              option.id,
            );
          }
          // Supprimer la question
          await webinarService.deleteSurveyQuestion(
            workspaceId,
            webinarId,
            templateId,
            questionToDelete.id,
          );
        }
      } else {
        // Le sondage n'existe pas, on le crée
        templateId = (await webinarService.createSurveyTemplate(
          workspaceId,
          webinarId,
          templateInput,
        )) as number;

        if (!templateId) {
          console.error('Impossible de créer le template de sondage');
          return;
        }

        // Créer toutes les questions et leurs options
        for (const [questionIndex, formQuestion] of data.questions.entries()) {
          const questionType =
            formQuestion.answerType === '2' ? 'MULTIPLE_CHOICE' : 'SINGLE_CHOICE';

          const questionId = (await webinarService.createSurveyQuestion(
            workspaceId,
            webinarId,
            templateId,
            {
              textContent: formQuestion.question.trim(),
              type: questionType,
              position: questionIndex,
              required: false,
              maxSelections: null,
              randomizeOptions: false,
            },
          )) as number;

          // Créer les options pour cette question
          for (const [answerIndex, answer] of formQuestion.answers.entries()) {
            if (answer.trim()) {
              await webinarService.createSurveyAnswerOption(
                workspaceId,
                webinarId,
                templateId,
                questionId,
                {
                  textContent: answer.trim(),
                  position: answerIndex,
                },
              );
            }
          }
        }
      }

      const updatedTemplate = await webinarService.getSurveyTemplate(
        workspaceId,
        webinarId,
        templateId,
      );
      setExistingSurveyTemplate(updatedTemplate);

      toast.success(t('waitingRoom.survey.saveSuccess'));
      setAccordionValue('');
    } catch (error) {
      console.error("Erreur lors de la sauvegarde du sondage de salle d'attente", error);

      const appError = normalizeAxiosError(error) as AppError;

      if (appError?.message) {
        const mappedMessage = mapHttpError(appError.message);
        const translatedMessage = t(mappedMessage, appError.message);
        toast.error(translatedMessage);
      } else {
        toast.error(t('errors.generic'));
      }

      if (appError?.details && Array.isArray(appError.details)) {
        appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
          if (detail.message) {
            const mappedMessage = mapHttpError(detail.message);
            const translatedMessage = t(mappedMessage, detail.message);
            toast.error(translatedMessage);
          }
        });
      }
    }
  };

  const currentQuestions = form.watch('questions');
  if (surveyStep > currentQuestions.length && currentQuestions.length > 0) {
    setSurveyStep(currentQuestions.length);
  }

  if (isLoading) {
    return (
      <div>
        <Accordion
          type="single"
          collapsible
          defaultValue={isEvergreen ? undefined : 'item-1'}
          className="space-y-[10px] rounded-[20px]"
        >
          <AccordionItem value="item-1" className="bg-medium p-6 rounded-[20px]">
            <AccordionTrigger className=" rounded-[20px] p-0">
              <Title level={2}>{t('waitingRoom.survey.title')}</Title>
            </AccordionTrigger>
            <AccordionContent className=" flex flex-col gap-4 rounded-[20px] bg-medium  pt-4 px-0">
              <div className="space-y-4">
                <Skeleton className="h-10 w-2/3" />
                <Skeleton className="h-10 w-2/3" />
                <Skeleton className="h-28 w-full rounded-[20px]" />
                <Skeleton className="h-28 w-full rounded-[20px]" />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  }

  const hasSurveyWaitingRoom = !!existingSurveyTemplate;

  return (
    <div className="bg-medium rounded-[20px] p-0">
      <Accordion
        key={`survey-accordion-${showSurvey}`}
        type="single"
        value={accordionValue}
        onValueChange={(value) => {
          setAccordionValue(value);
        }}
        collapsible
        className="space-y-[10px] rounded-[20px]"
      >
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 rounded-[20px]"
          >
            <AccordionItem value="item-1" className="bg-medium p-6 rounded-[20px]">
              <div className="flex flex-col gap-4">
                <AccordionTrigger className="rounded-[20px] p-0" fillForm={hasSurveyWaitingRoom}>
                  <Title level={2}>{t('waitingRoom.survey.title')}</Title>
                </AccordionTrigger>
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <TextFieldWithLimit
                          label={t('waitingRoom.survey.titleLabel')}
                          className="bg-white"
                          value={field.value ?? ''}
                          onChange={(v) => field.onChange(v)}
                          maxLength={100}
                          error={fieldState.error?.message}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="subtitle"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormControl>
                        <TextFieldWithLimit
                          label={t('waitingRoom.survey.subtitleLabel')}
                          className="bg-white"
                          value={field.value ?? ''}
                          onChange={(v) => field.onChange(v)}
                          maxLength={200}
                          error={fieldState.error?.message}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <AccordionContent className=" flex flex-col gap-4 rounded-[20px] bg-medium  pt-4 px-0">
                <Separator className="bg-input" />

                {form.watch('questions').map((question, questionIndex) => (
                  <div key={question.id} className="space-y-4">
                    <div className=" flex flex-col lg:flex-row items-start gap-4">
                      <div className=" w-full lg:w-2/3 flex items-start gap-2">
                        <FormField
                          control={form.control}
                          name={`questions.${questionIndex}.question`}
                          render={({ field, fieldState }) => (
                            <FormItem className="w-full">
                              <FormControl>
                                <TextFieldWithLimit
                                  label={t('waitingRoom.survey.questionLabel', {
                                    number: questionIndex + 1,
                                  })}
                                  value={field.value ?? ''}
                                  onChange={(v) => field.onChange(v)}
                                  className="bg-white"
                                  maxLength={300}
                                  error={fieldState.error?.message}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        {form.watch('questions').length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveQuestion(question.id)}
                            className="block lg:hidden bg-white h-14 rounded-lg py-3 px-2 border border-input cursor-pointer"
                          >
                            <Trash2 className="size-6" />
                          </button>
                        )}
                      </div>
                      <div className="w-full lg:w-1/3">
                        <FormField
                          control={form.control}
                          name={`questions.${questionIndex}.answerType`}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Select value={field.value} onValueChange={field.onChange}>
                                  <SelectTrigger
                                    label={t('waitingRoom.survey.answerTypeLabel')}
                                    className="w-full rounded-lg border h-14 border-input"
                                  >
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="1">
                                      {t('waitingRoom.survey.singleChoice')}
                                    </SelectItem>
                                    <SelectItem value="2">
                                      {t('waitingRoom.survey.multipleChoice')}
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </FormControl>
                              <FormMessage className="text-left text-red-500 text-sm" />
                            </FormItem>
                          )}
                        />
                      </div>

                      {form.watch('questions').length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveQuestion(question.id)}
                          className="hidden lg:block bg-white h-14 rounded-lg py-3 px-2 border border-input cursor-pointer"
                        >
                          <Trash2 className="size-6" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-3">
                      {question.answers.map((_answer, answerIndex) => (
                        <div key={answerIndex} className="flex items-center gap-2">
                          <div className="flex-1">
                            <FormField
                              control={form.control}
                              name={`questions.${questionIndex}.answers.${answerIndex}`}
                              render={({ field, fieldState }) => (
                                <FormItem>
                                  <FormControl>
                                    <TextFieldWithLimit
                                      label={t('waitingRoom.survey.answerLabel', {
                                        number: answerIndex + 1,
                                      })}
                                      value={field.value ?? ''}
                                      onChange={(v) => field.onChange(v)}
                                      className="bg-white"
                                      maxLength={300}
                                      error={fieldState.error?.message}
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          </div>
                          {question.answers.length > 2 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveAnswer(question.id, answerIndex)}
                              className=" bg-white rounded-lg py-3 px-2 border border-input cursor-pointer"
                            >
                              <Trash2 className="size-6" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {question.answers.length < 6 && (
                      <button
                        type="button"
                        onClick={() => handleAddAnswer(question.id)}
                        className="flex items-center gap-2 text-sm cursor-pointer"
                      >
                        <Plus size={16} />
                        {t('waitingRoom.survey.addAnswer')}
                      </button>
                    )}

                    {questionIndex < form.watch('questions').length - 1 && (
                      <Separator className="bg-input" />
                    )}
                  </div>
                ))}

                <Separator className="bg-input" />

                {form.watch('questions').length < 4 && (
                  <button
                    type="button"
                    onClick={handleAddQuestion}
                    className="flex items-center gap-2 text-base cursor-pointer"
                  >
                    <Plus size={16} />
                    {t('waitingRoom.survey.addQuestion')}
                  </button>
                )}

                <Button
                  type="submit"
                  disabled={form.formState.isSubmitting || !form.formState.isValid}
                  className="w-full disabled:bg-gray-200"
                >
                  {form.formState.isSubmitting && <Loader />} {tCW('save')}
                </Button>
                <Accordion
                  type="single"
                  defaultValue={isEvergreen ? undefined : 'preview-1'}
                  collapsible
                  className="pb-0"
                >
                  <AccordionItem value="preview-1">
                    <AccordionTrigger className=" rounded-[20px] ">
                      <Title level={2}>{t('waitingRoom.survey.preview')}</Title>
                    </AccordionTrigger>

                    <AccordionContent className="flex py-0  flex-col gap-4 rounded-[20px] pt-4 px-0">
                      <PreviewActivitySurvey
                        form={form}
                        visualIdentity={visualIdentity}
                        currentWaitingRoomPage={currentWaitingRoomPage}
                        surveyStep={surveyStep}
                        setSurveyStep={setSurveyStep}
                        selectedAnswers={selectedAnswers}
                        setSelectedAnswers={setSelectedAnswers}
                      />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          </form>
        </Form>
      </Accordion>
    </div>
  );
}
